import {isEmpty, isNil, clone} from 'ramda';
import {transferDateObj, formatYYYYMMDD} from 'app/gakki/utils/time';
import {DateTime} from 'luxon';
import {isNationalIdentificationNumberValid} from 'taiwan-id-validator';
import {isInvalidID} from 'app/gakki/utils/validate';
import {Occupational_Injury_Insurance_EffectiveDate} from 'const';
import {foodAllowanceUpperLimit} from '@gakki/api/types';
import {formatCurrency} from '@gakki/utils/format';

const required = (input, fieldName) =>
  isEmpty(input) || isNil(input) ? `${fieldName}為必填` : null;
const checkPercentRange = (num, min = 0, max = 100) =>
  num > max ? `最大值為${max}` : num < min ? `最小值為${min}` : null;
const checkIdRule = (id, nationality) => {
  return (nationality === 0 && !isNationalIdentificationNumberValid(id)) ||
    (nationality === 1 && isInvalidID(id, true))
    ? '證件號碼錯誤'
    : null;
};
const checkSubjectExist = (subjectId, salaryAccounts) => {
  return !salaryAccounts.find(s => s.salaryAccountId === subjectId)
    ? '薪資科目不存在'
    : null;
};
const isNullArray = array => array.every(i => i === null);

const deleteEmptyKey = obj => {
  const newObj = clone(obj);
  Object.entries(newObj).forEach(([key, value]) => {
    if (!value) {
      delete newObj[key];
    }
  });
  return newObj;
};

export const isAfterDate = (date, comparison, errorMessage) => {
  let comparisonObj = transferDateObj(comparison);
  let dateObj = transferDateObj(date);
  if (comparisonObj && dateObj) {
    return DateTime.fromJSDate(comparisonObj) <= DateTime.fromJSDate(dateObj)
      ? null
      : errorMessage;
  }
  return null;
};

export const validate = (data, options) => {
  const {
    personalPayroll,
    payrollSubject: {payrollSubjectItems},
    laborInsurance,
    occupationalInjuryInsurance,
    retirement,
    healthInsurance,
    dependentsHealthInsurance: {
      dependentsHealthInsuranceItems,
      enabled: dependentsNhiEnabled,
    },
    tax,
    dependentsTax: {dependentsTaxItems, enabled: dependentsTaxEnabled},
    employee,
  } = data;
  const {salaryAccounts} = options;
  const onBoardingDate = DateTime.fromJSDate(
    new Date(employee?.onBoardingDate),
  ).toLocaleString(DateTime.DATE_SHORT);

  const personalPayrollErrors = deleteEmptyKey({
    effectiveDate: personalPayroll.enabled
      ? required(personalPayroll.effectiveDate, '生效日期') ||
        isAfterDate(
          personalPayroll.effectiveDate,
          onBoardingDate,
          `不可早於到職日${onBoardingDate}`,
        )
      : null,
    bankId: personalPayroll.enabled
      ? required(personalPayroll.bankId, '薪資銀行')
      : null,
  });

  const subjectItemErrors = payrollSubjectItems.map(subject => {
    const foodAllowanceCode = '1001';
    const isFoodAllowanceLimit =
      subject.salaryAccountCode === foodAllowanceCode &&
      subject.money > foodAllowanceUpperLimit
        ? `伙食津貼上限${formatCurrency(foodAllowanceUpperLimit)}`
        : null;
    const subjectErrors = deleteEmptyKey({
      salaryAccountId:
        required(subject.salaryAccountId, '科目') ||
        checkSubjectExist(subject.salaryAccountId, salaryAccounts),
      money: required(subject.money, '金額') || isFoodAllowanceLimit,
    });
    return isEmpty(subjectErrors) ? null : subjectErrors;
  });

  const payrollSubjectErrors = deleteEmptyKey({
    payrollSubjectItems:
      isNullArray(subjectItemErrors) || !personalPayroll.enabled
        ? null
        : subjectItemErrors,
  });

  const laborInsuranceErrors = deleteEmptyKey({
    effectiveDate: laborInsurance.enabled
      ? required(laborInsurance.effectiveDate, '生效日期') ||
        isAfterDate(
          laborInsurance.effectiveDate,
          onBoardingDate,
          `不可早於到職日${onBoardingDate}`,
        )
      : null,
    insuranceApplicantUnitId: laborInsurance.enabled
      ? required(laborInsurance.insuranceApplicantUnitId, '投保單位')
      : null,
    insuranceIdentityId: laborInsurance.enabled
      ? required(laborInsurance.insuranceIdentityId, '投保身分')
      : null,
    insuranceAmountLevelId: laborInsurance.enabled
      ? required(laborInsurance.insuranceAmountLevelId, '投保金額')
      : null,
    allowanceRate:
      laborInsurance.enabled && laborInsurance.isDeduction
        ? required(laborInsurance.allowanceRate, '補助費率') ||
          checkPercentRange(laborInsurance.allowanceRate)
        : null,
  });

  const minDate =
    onBoardingDate &&
    new Date(onBoardingDate) >
      new Date(Occupational_Injury_Insurance_EffectiveDate)
      ? onBoardingDate
      : Occupational_Injury_Insurance_EffectiveDate;
  const occupationalInjuryInsuranceErrors = deleteEmptyKey({
    effectiveDate: occupationalInjuryInsurance.enabled
      ? required(occupationalInjuryInsurance.effectiveDate, '生效日期') ||
        isAfterDate(
          occupationalInjuryInsurance.effectiveDate,
          minDate,
          `不可早於 ${minDate}`,
        )
      : null,
    insuranceApplicantUnitId: occupationalInjuryInsurance.enabled
      ? required(
          occupationalInjuryInsurance.insuranceApplicantUnitId,
          '投保單位',
        )
      : null,
    insuranceIdentityId: occupationalInjuryInsurance.enabled
      ? required(occupationalInjuryInsurance.insuranceIdentityId, '投保身分')
      : null,
    insuranceAmountLevelId: occupationalInjuryInsurance.enabled
      ? required(occupationalInjuryInsurance.insuranceAmountLevelId, '投保金額')
      : null,
    allowanceRate:
      occupationalInjuryInsurance.enabled &&
      occupationalInjuryInsurance.isDeduction
        ? required(occupationalInjuryInsurance.allowanceRate, '補助費率') ||
          checkPercentRange(occupationalInjuryInsurance.allowanceRate)
        : null,
  });

  const laborPensionErrors = deleteEmptyKey({
    effectiveDate: retirement.enabled
      ? required(retirement.effectiveDate, '生效日期') ||
        isAfterDate(
          retirement.effectiveDate,
          onBoardingDate,
          `不可早於到職日${onBoardingDate}`,
        )
      : null,
    insuranceApplicantUnitId:
      retirement.enabled && retirement.category === 1
        ? required(retirement.insuranceApplicantUnitId, '投保單位')
        : null,
    insuranceAmountLevelId:
      retirement.enabled && retirement.category === 1
        ? required(retirement.insuranceAmountLevelId, '投保金額')
        : null,
    employerBurdenRate:
      retirement.enabled && retirement.category === 1
        ? required(retirement.employerBurdenRate, '雇主提繳率') ||
          checkPercentRange(retirement.employerBurdenRate, 0, 100)
        : null,
    personalBurdenRate:
      retirement.enabled && retirement.category === 1
        ? required(retirement.personalBurdenRate, '個人提繳率') ||
          checkPercentRange(retirement.personalBurdenRate, 0, 6)
        : null,
  });

  const nhiErrors = deleteEmptyKey({
    effectiveDate: healthInsurance.enabled
      ? required(healthInsurance.effectiveDate, '生效日期') ||
        isAfterDate(
          healthInsurance.effectiveDate,
          onBoardingDate,
          `不可早於到職日${onBoardingDate}`,
        )
      : null,
    insuranceApplicantUnitId: healthInsurance.enabled
      ? required(healthInsurance.insuranceApplicantUnitId, '投保單位')
      : null,
    insuranceAmountLevelId: healthInsurance.enabled
      ? required(healthInsurance.insuranceAmountLevelId, '投保金額')
      : null,
    allowanceRate:
      healthInsurance.enabled &&
      healthInsurance.isDeduction &&
      healthInsurance.deductionRateType === 0
        ? required(healthInsurance.allowanceRate, '補助費率') ||
          checkPercentRange(healthInsurance.allowanceRate)
        : null,
    highestAllowanceAmount:
      healthInsurance.enabled &&
      healthInsurance.isDeduction &&
      healthInsurance.deductionRateType === 1
        ? required(healthInsurance.highestAllowanceAmount, '最高補助金額')
        : null,
  });

  const dependentsHealthInsuranceItemsErrors =
    dependentsHealthInsuranceItems.map((dependent, index) => {
      const dependentErrors = deleteEmptyKey({
        name: dependentsNhiEnabled
          ? required(dependent.name, '眷屬姓名')
          : null,
        identityNo: dependentsNhiEnabled
          ? required(dependent.identityNo, '證件號碼') ||
            checkIdRule(dependent.identityNo, dependent.nationality) ||
            (dependentsHealthInsuranceItems.some(
              (f, compareIdx) =>
                f.identityNo === dependent.identityNo && compareIdx !== index,
            )
              ? '證件號碼不可重覆'
              : null)
          : null,
        birthday: dependentsNhiEnabled
          ? required(dependent.birthday, '生日') ||
            isAfterDate(
              dependent.addInsuranceDate,
              dependent.birthday,
              `不可晚於加保日${formatYYYYMMDD(dependent.addInsuranceDate)}`,
            )
          : null,
        nHIDependentsAddTitleCode: dependentsNhiEnabled
          ? required(dependent.nHIDependentsAddTitleCode, '稱謂')
          : null,
        allowanceRate:
          dependentsNhiEnabled &&
          dependent.isDeductionType &&
          dependent.deductionRateType === 0
            ? required(dependent.allowanceRate, '補助費率') ||
              checkPercentRange(dependent.allowanceRate)
            : null,
        highestAllowanceAmount:
          dependentsNhiEnabled &&
          dependent.isDeductionType &&
          dependent.deductionRateType === 1
            ? required(dependent.highestAllowanceAmount, '最高補助金額')
            : null,
        addInsuranceDate: dependentsNhiEnabled
          ? required(dependent.addInsuranceDate, '加保日') ||
            isAfterDate(
              dependent.addInsuranceDate,
              onBoardingDate,
              `不可早於員工到職日${onBoardingDate}`,
            )
          : null,
      });
      return isEmpty(dependentErrors) ? null : dependentErrors;
    });

  const dependentsHealthInsuranceErrors = deleteEmptyKey({
    dependentsHealthInsuranceItems: isNullArray(
      dependentsHealthInsuranceItemsErrors,
    )
      ? null
      : dependentsHealthInsuranceItemsErrors,
  });

  const taxErrors = deleteEmptyKey({
    effectiveDate: tax.enabled
      ? required(tax.effectiveDate, '生效日期') ||
        isAfterDate(
          tax.effectiveDate,
          onBoardingDate,
          `不可早於到職日${onBoardingDate}`,
        )
      : null,
    incomeTaxReturnUnitId: tax.enabled
      ? required(tax.incomeTaxReturnUnitId, '申報單位')
      : null,
    incomeTaxNumberCode: tax.enabled
      ? required(tax.incomeTaxNumberCode, '證號別')
      : null,
    withHoldingTypeCode:
      tax.enabled &&
      /(IncomeTaxNumber01|IncomeTaxNumber02)/.test(tax.incomeTaxNumberCode)
        ? required(tax.withHoldingTypeCode, '扣繳方式')
        : null,
    supplementaryPremiumPartTimeUnitId:
      tax.enabled && Boolean(tax.supplementaryPremiumPartTime)
        ? required(
            tax.supplementaryPremiumPartTimeUnitId,
            '兼職薪資補充保費單位',
          )
        : null,
  });

  const dependentsTaxItemsErrors = dependentsTaxItems.map(
    (dependent, index) => {
      const dependentErrors = deleteEmptyKey({
        name: dependentsTaxEnabled
          ? required(dependent.name, '眷屬姓名')
          : null,
        dependentsTitle: dependentsTaxEnabled
          ? required(dependent.dependentsTitle, '稱謂')
          : null,
        birthday: dependentsTaxEnabled
          ? required(dependent.birthday, '生日')
          : null,
        identityNo: dependentsTaxEnabled
          ? required(dependent.identityNo, '證件號碼') ||
            (dependentsTaxItems.some(
              (f, compareIdx) =>
                f.identityNo === dependent.identityNo && compareIdx !== index,
            )
              ? '證件號碼不可重覆'
              : null)
          : null,
        address: dependentsTaxEnabled
          ? required(dependent.address, '現居地址')
          : null,
        dependentsExemptionConditionCode: dependentsTaxEnabled
          ? required(dependent.dependentsExemptionConditionCode, '符合條件')
          : null,
      });
      return isEmpty(dependentErrors) ? null : dependentErrors;
    },
  );

  const dependentsTaxErrors = deleteEmptyKey({
    dependentsTaxItems: isNullArray(dependentsTaxItemsErrors)
      ? null
      : dependentsTaxItemsErrors,
  });

  const errors = deleteEmptyKey({
    personalPayroll: isEmpty(personalPayrollErrors)
      ? null
      : personalPayrollErrors,
    payrollSubject: isEmpty(payrollSubjectErrors) ? null : payrollSubjectErrors,
    laborInsurance: isEmpty(laborInsuranceErrors) ? null : laborInsuranceErrors,
    occupationalInjuryInsurance: isEmpty(occupationalInjuryInsuranceErrors)
      ? null
      : occupationalInjuryInsuranceErrors,
    retirement: isEmpty(laborPensionErrors) ? null : laborPensionErrors,
    healthInsurance: isEmpty(nhiErrors) ? null : nhiErrors,
    dependentsHealthInsurance: isEmpty(dependentsHealthInsuranceErrors)
      ? null
      : dependentsHealthInsuranceErrors,
    tax: isEmpty(taxErrors) ? null : taxErrors,
    dependentsTax: isEmpty(dependentsTaxErrors) ? null : dependentsTaxErrors,
  });
  return isEmpty(errors) ? null : errors;
};
